import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Card, CardContent } from '@leoclinic/ui';
import { useAuth } from '@leoclinic/hooks';
import { BRAND_COLORS } from '@leoclinic/types';
import { registerSchema, type RegisterFormData } from '../schemas/auth.schema';
import { useClinicPortal } from '@/providers/clinic-provider';

function getRegisterErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.message === 'EMAIL_CONFIRMATION_REQUIRED') {
      return 'EMAIL_CONFIRMATION_REQUIRED';
    }
    const msg = err.message.toLowerCase();
    if (msg.includes('rate limit') || msg.includes('email rate limit')) {
      return 'Limite de emails atingido. Aguarde alguns minutos ou desactive a confirmação de email no Supabase (Authentication → Providers → Email).';
    }
    if (msg.includes('already registered')) {
      return 'Este email já está registado. Tente entrar.';
    }
    return err.message;
  }
  return 'Não foi possível criar a conta. Tente novamente.';
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { clinic } = useClinicPortal();
  const { signUp, isSigningUp } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { full_name: '', email: '', password: '', confirm_password: '' },
  });

  const onSubmit = async (data: RegisterFormData) => {
    setErrorMessage(null);
    setConfirmationSent(false);

    try {
      await signUp({ email: data.email, password: data.password, fullName: data.full_name });
      navigate('/minhas-marcacoes', { replace: true });
    } catch (err) {
      const message = getRegisterErrorMessage(err);
      if (message === 'EMAIL_CONFIRMATION_REQUIRED') {
        setConfirmationSent(true);
        return;
      }
      setErrorMessage(message);
    }
  };

  if (confirmationSent) {
    return (
      <div className="min-h-screen bg-[#F6F3EE] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-[#ECECEC]">
          <CardContent className="pt-8 pb-8 space-y-4 text-center">
            <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.greenDark }}>
              Verifique o seu email
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Enviámos um link de confirmação. Abra o email, confirme a conta e depois entre em
              {' '}{clinic?.name ?? 'a clínica'}.
            </p>
            <Button asChild className="w-full">
              <Link to="/login">Ir para entrar</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F3EE] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#ECECEC]">
        <CardContent className="pt-8 pb-8 space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.greenDark }}>
              Criar conta
            </h1>
            <p className="text-sm text-muted-foreground">
              Use o mesmo email das suas marcações — {clinic?.name ?? 'a clínica'}
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Nome completo"
              placeholder="O seu nome"
              error={errors.full_name?.message}
              {...register('full_name')}
            />
            <Input
              label="Email"
              type="email"
              placeholder="nome@email.com"
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Password"
              type="password"
              placeholder="Mínimo 6 caracteres"
              error={errors.password?.message}
              {...register('password')}
            />
            <Input
              label="Confirmar password"
              type="password"
              placeholder="Repita a password"
              error={errors.confirm_password?.message}
              {...register('confirm_password')}
            />
            {errorMessage && (
              <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                {errorMessage}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={isSigningUp}>
              {isSigningUp ? 'A criar...' : 'Criar conta'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            Já tem conta?{' '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: BRAND_COLORS.greenDark }}>
              Entrar
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
